import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Camera,
  Upload,
  Loader2,
  RotateCcw,
  ScanLine,
  Crown,
  Lock,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { todayISO } from "@/lib/format";
import { maybeAlertBudgetForExpense } from "@/lib/budgetAlerts";

const STATE = {
  IDLE: "idle",
  PROCESSING: "processing",
  REVIEW: "review",
  SAVING: "saving",
};

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

export default function OcrEntryDialog({
  open,
  onOpenChange,
  categories,
  onCreated,
  isPremium,
  onUpgrade,
}) {
  const [state, setState] = useState(STATE.IDLE);
  const [previewUrl, setPreviewUrl] = useState("");
  const [merchant, setMerchant] = useState("");
  const [type, setType] = useState("expense");
  const [category, setCategory] = useState("");
  const [categorySource, setCategorySource] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const filteredCategories = (categories || []).filter((c) => c.type === type);

  // Load accounts when dialog opens
  useEffect(() => {
    if (!open) return;
    api
      .get("/accounts")
      .then((r) => {
        const list = r.data || [];
        setAccounts(list);
        if (list.length > 0 && !list.find((a) => a.id === accountId)) {
          const def = list.find((a) => a.is_default) || list[0];
          setAccountId(def.id);
        }
      })
      .catch(() => setAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetAll = useCallback(() => {
    setState(STATE.IDLE);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setMerchant("");
    setType("expense");
    setCategory("");
    setCategorySource("");
    setAmount("");
    setDate(todayISO());
    setNote("");
  }, [previewUrl]);

  useEffect(() => {
    if (!open) {
      resetAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (state !== STATE.REVIEW) return;
    if (filteredCategories.length > 0 && !filteredCategories.find((c) => c.name === category)) {
      setCategory(filteredCategories[0].name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, state]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error("Formato no soportado. Usa JPG, PNG, WEBP o HEIC.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("La imagen es demasiado grande (máx 10MB).");
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
    await uploadImage(file);
  };

  const uploadImage = async (file) => {
    setState(STATE.PROCESSING);
    try {
      const fd = new FormData();
      fd.append("image", file, file.name || "receipt.jpg");
      const res = await api.post("/transactions/ocr", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const draft = res.data?.draft || {};
      setType(draft.type === "income" ? "income" : "expense");
      setCategory(draft.category || "");
      setCategorySource(draft.category_source || "");
      setAmount(draft.amount ? String(draft.amount) : "");
      setMerchant(draft.merchant || "");
      setDate(draft.date || todayISO());
      setNote(draft.note || "");
      setState(STATE.REVIEW);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 402) {
        toast.error("Función Premium. Suscríbete para escanear recibos.");
        onOpenChange?.(false);
        onUpgrade?.();
        return;
      }
      toast.error(formatApiErrorDetail(detail) || "No se pudo procesar la imagen");
      setState(STATE.IDLE);
    }
  };

  const handleRetry = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setState(STATE.IDLE);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!category) {
      toast.error("Selecciona una categoría");
      return;
    }
    if (!accountId) {
      toast.error("Selecciona una cuenta");
      return;
    }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("El monto debe ser mayor a 0");
      return;
    }
    setState(STATE.SAVING);
    try {
      await api.post("/transactions", { type, category, amount: amt, date, note, source: "ocr", account_id: accountId });
      toast.success(type === "income" ? "Ingreso registrado desde recibo" : "Gasto registrado desde recibo");
      if (type === "expense") {
        maybeAlertBudgetForExpense({ category, date });
      }
      onOpenChange?.(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
      setState(STATE.REVIEW);
    }
  };

  // Premium gate
  if (open && !isPremium) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#131C2E] max-w-md" data-testid="ocr-premium-gate">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#F1F5F9] flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#B45309]" />
              Escanear recibos
            </DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">
              Función Premium con tecnología de visión IA.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="rounded-md bg-[#FFFBEB] border border-[#FDE68A] p-4">
              <p className="text-sm text-[#92400E] font-medium mb-1 flex items-center gap-1">
                <Crown className="w-4 h-4" /> Solo Premium
              </p>
              <p className="text-xs text-[#78350F]">
                Toma una foto a tu ticket y registramos automáticamente el monto, comercio,
                fecha y categoría. Ahorra tiempo en cada compra.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 flex-row sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              className="border-[#2A3447]"
              data-testid="ocr-cancel-gate"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => {
                onOpenChange?.(false);
                onUpgrade?.();
              }}
              className="bg-[#B45309] hover:bg-[#92400E] text-white"
              data-testid="ocr-upgrade-button"
            >
              <Crown className="w-4 h-4 mr-1" /> Suscribirme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131C2E] max-w-md" data-testid="ocr-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#F1F5F9] flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-[#0052FF]" />
            Escanear recibo
          </DialogTitle>
          <DialogDescription className="text-sm text-[#64748B]">
            Toma una foto o sube una imagen del ticket para extraer los datos.
          </DialogDescription>
        </DialogHeader>

        {/* IDLE */}
        {state === STATE.IDLE && (
          <div className="py-4 flex flex-col gap-3" data-testid="ocr-idle">
            <p className="text-sm text-center text-[#64748B] max-w-xs mx-auto">
              Selecciona una imagen del recibo. Detectamos monto, comercio, fecha y categoría.
            </p>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="ocr-camera-input"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="ocr-file-input"
            />
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className="bg-[#0052FF] hover:bg-[#0040CC] text-white h-20 flex-col gap-1"
                data-testid="ocr-take-photo"
              >
                <Camera className="w-6 h-6" />
                <span className="text-xs">Tomar foto</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="border-[#2A3447] h-20 flex-col gap-1"
                data-testid="ocr-upload-image"
              >
                <Upload className="w-6 h-6" />
                <span className="text-xs">Subir imagen</span>
              </Button>
            </div>
            <p className="text-[10px] text-center text-[#94A3B8]">
              JPG, PNG, WEBP o HEIC · máx 10MB
            </p>
          </div>
        )}

        {/* PROCESSING */}
        {state === STATE.PROCESSING && (
          <div className="py-10 flex flex-col items-center gap-3" data-testid="ocr-processing">
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Recibo"
                className="max-h-40 rounded-md border border-[#2A3447] object-contain"
              />
            )}
            <Loader2 className="w-8 h-8 animate-spin text-[#0052FF]" />
            <p className="text-sm text-[#64748B]">Analizando recibo con IA…</p>
          </div>
        )}

        {/* REVIEW + SAVING */}
        {(state === STATE.REVIEW || state === STATE.SAVING) && (
          <form onSubmit={handleSave} className="space-y-4 mt-1" data-testid="ocr-review">
            {previewUrl && (
              <div className="relative rounded-md border border-[#2A3447] bg-[#131C2E] p-2 flex items-center gap-3">
                <img
                  src={previewUrl}
                  alt="Recibo"
                  className="h-16 w-16 rounded object-cover"
                  data-testid="ocr-preview-image"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">Comercio</p>
                  <p className="text-sm text-[#F1F5F9] font-medium truncate" data-testid="ocr-merchant">
                    {merchant || "No detectado"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRetry}
                  disabled={state === STATE.SAVING}
                  className="text-[#94A3B8] hover:text-[#EF4444]"
                  aria-label="Quitar imagen"
                  data-testid="ocr-remove-image"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <Tabs value={type} onValueChange={setType}>
              <TabsList className="grid grid-cols-2 w-full bg-[#1B2638]">
                <TabsTrigger
                  value="expense"
                  className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white"
                  data-testid="ocr-tab-expense"
                >
                  Gasto
                </TabsTrigger>
                <TabsTrigger
                  value="income"
                  className="data-[state=active]:bg-[#0052FF] data-[state=active]:text-white"
                  data-testid="ocr-tab-income"
                >
                  Ingreso
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Cuenta</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger
                  className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
                  data-testid="ocr-account-select"
                >
                  <SelectValue placeholder="Selecciona cuenta" />
                </SelectTrigger>
                <SelectContent className="bg-[#131C2E] max-h-64">
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: a.color }}
                        />
                        {a.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Categoría</Label>
              <Select
                value={category}
                onValueChange={(v) => {
                  setCategory(v);
                  setCategorySource("");
                }}
              >
                <SelectTrigger
                  className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
                  data-testid="ocr-category-select"
                >
                  <SelectValue placeholder="Selecciona categoría" />
                </SelectTrigger>
                <SelectContent className="bg-[#131C2E] max-h-64">
                  {filteredCategories.map((c) => (
                    <SelectItem key={c.id} value={c.name}>
                      <span className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: c.color }}
                        />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {categorySource === "heuristic" && (
                <p
                  className="mt-1.5 text-[11px] text-[#0052FF] flex items-center gap-1"
                  data-testid="ocr-category-source-hint"
                >
                  <ScanLine className="h-3 w-3" />
                  Categoría sugerida automáticamente por el comercio
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Monto</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="mt-2 h-11 bg-[#131C2E] border-[#2A3447] font-numeric text-lg"
                  required
                  data-testid="ocr-amount-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Fecha</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
                  required
                  data-testid="ocr-date-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Nota</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Notas o referencia..."
                className="mt-2 bg-[#131C2E] border-[#2A3447] resize-none"
                rows={2}
                data-testid="ocr-note-input"
              />
            </div>

            <DialogFooter className="gap-2 flex-row sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={handleRetry}
                className="border-[#2A3447]"
                disabled={state === STATE.SAVING}
                data-testid="ocr-retry-button"
              >
                <RotateCcw className="w-4 h-4 mr-1" /> Otro recibo
              </Button>
              <Button
                type="submit"
                disabled={state === STATE.SAVING}
                className="bg-[#0052FF] hover:bg-[#0040CC] text-white"
                data-testid="ocr-save-button"
              >
                {state === STATE.SAVING ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        )}

        {state === STATE.IDLE && (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              className="border-[#2A3447]"
              data-testid="ocr-close-button"
            >
              Cerrar
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
